import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bug, 
  Phone, 
  Mail, 
  MapPin, 
  Smartphone, 
  Monitor, 
  Gamepad2, 
  Cpu,
  ChevronDown,
  Menu,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  {
    id: "phone-tablet",
    title: "Phone & Tablet Repair",
    description: "Screen replacements, battery swaps, charging port repairs, and water damage recovery for all major brands.",
    icon: Smartphone,
    image: "https://images.unsplash.com/photo-1712600096579-afb75d1b72ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwaG9uZSUyMHJlcGFpciUyMGluc2lkZSUyMGNvbXBvbmVudHMlMjBjaXJjdWl0JTIwYm9hcmR8ZW58MHx8fHwxNzc1MTU3NzUwfDA&ixlib=rb-4.1.0&q=85",
    span: "col-span-full lg:col-span-8"
  },
  {
    id: "computer",
    title: "Computer Repair & Builds",
    description: "Custom PC builds, hardware upgrades, virus removal, and performance optimization.",
    icon: Monitor,
    image: "https://images.unsplash.com/photo-1580090918915-b2678c8a3d5a",
    span: "col-span-full lg:col-span-4"
  },
  {
    id: "console",
    title: "Console Repair & Modification",
    description: "HDMI port repairs, disc drive fixes, controller repairs, and custom modifications for modern consoles.",
    icon: Gamepad2,
    image: "https://images.pexels.com/photos/4219889/pexels-photo-4219889.jpeg",
    span: "col-span-full lg:col-span-6"
  },
  {
    id: "retro",
    title: "Retro Console Restoration",
    description: "Capacitor replacements, RGB mods, region unlocks, and full restorations for classic gaming systems.",
    icon: Cpu,
    image: "https://images.pexels.com/photos/4219885/pexels-photo-4219885.jpeg",
    span: "col-span-full lg:col-span-6"
  }
];

const faqItems = [
  {
    question: "How long does a typical repair take?",
    answer: "Most repairs are completed within 24-72 hours. Simple repairs like screen replacements can often be done same-day. For more complex issues or retro console restorations, we'll provide a specific timeline after diagnosis."
  },
  {
    question: "Do you use OEM (Original Equipment Manufacturer) parts?",
    answer: "We offer both OEM and high-quality aftermarket options. We'll discuss the best choice for your specific situation, considering factors like availability, warranty, and your preferences."
  },
  {
    question: "What warranty do you offer on repairs?",
    answer: "All our repairs come with a 90-day warranty covering both parts and labor. If the same issue recurs within this period, we'll fix it at no additional cost."
  },
  {
    question: "Can you recover data from a damaged device?",
    answer: "In many cases, yes. We have specialized tools and techniques for data recovery from phones, computers, and storage devices. Success depends on the extent of the damage, which we'll assess during diagnosis."
  },
  {
    question: "Do I need an appointment?",
    answer: "Walk-ins are welcome for quick diagnostics and simple repairs. For larger projects like custom PC builds or retro console restorations, we recommend scheduling an appointment so we can dedicate proper time to your project."
  },
  {
    question: "What retro consoles do you work on?",
    answer: "We service a wide range of classic systems including NES, SNES, Sega Genesis, N64, PlayStation 1 & 2, original Xbox, Dreamcast, and many more. If you have a vintage system, chances are we can help."
  },
  {
    question: "Can you mod my console without voiding warranties?",
    answer: "For retro consoles, warranties are typically no longer a concern. For newer consoles, we'll clearly explain which modifications may affect your warranty so you can make an informed decision."
  }
];

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-100">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-slate-800" data-testid="header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="logo-placeholder text-xl md:text-2xl" data-testid="logo">
              <span className="text-slate-100">CYBRDYNE</span>
              <span className="text-red-500">_</span>
              <span className="text-slate-400">SYSTEMS</span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
              <button onClick={() => scrollToSection("services")} className="nav-link text-slate-400 hover:text-slate-100 transition-colors text-sm uppercase tracking-widest">
                Services
              </button>
              <button onClick={() => scrollToSection("faq")} className="nav-link text-slate-400 hover:text-slate-100 transition-colors text-sm uppercase tracking-widest">
                FAQ
              </button>
              <button onClick={() => scrollToSection("contact")} className="nav-link text-slate-400 hover:text-slate-100 transition-colors text-sm uppercase tracking-widest">
                Contact
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-slate-400 hover:text-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-slate-800" data-testid="mobile-nav">
              <div className="flex flex-col space-y-4">
                <button onClick={() => scrollToSection("services")} className="text-slate-400 hover:text-slate-100 transition-colors text-sm uppercase tracking-widest text-left">
                  Services
                </button>
                <button onClick={() => scrollToSection("faq")} className="text-slate-400 hover:text-slate-100 transition-colors text-sm uppercase tracking-widest text-left">
                  FAQ
                </button>
                <button onClick={() => scrollToSection("contact")} className="text-slate-400 hover:text-slate-100 transition-colors text-sm uppercase tracking-widest text-left">
                  Contact
                </button>
              </div>
            </nav>
          )}
        </div>
        
        {/* Hidden Bug #1 - Header corner */}
        <Bug className="bug-easter-egg absolute top-4 right-4 w-4 h-4 hidden lg:block" data-testid="bug-1" />
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1476316/pexels-photo-1476316.jpeg"
            alt=""
            className="w-full h-full object-cover hero-bg-image"
          />
          <div className="hero-gradient-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-6">
              Central Florida's Tech Repair Specialists
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-slate-100 mb-6">
              CYBRDYNE<span className="text-red-500">.</span>
              <br />
              SYSTEMS
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              Expert repair and modification services for phones, computers, and gaming consoles. 
              From modern devices to retro classics, we bring your tech back to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection("contact")}
                className="btn-primary bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-widest px-8 py-6 rounded-none"
                data-testid="hero-cta-btn"
              >
                Get a Quote
              </Button>
              <Button 
                onClick={() => scrollToSection("services")}
                variant="outline"
                className="border-slate-600 hover:border-slate-400 text-slate-300 hover:text-white font-bold uppercase tracking-widest px-8 py-6 rounded-none bg-transparent"
                data-testid="hero-services-btn"
              >
                Our Services
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-slate-600 w-8 h-8" />
        </div>

        {/* Hidden Bug #2 - Hero bottom */}
        <Bug className="bug-easter-egg absolute bottom-16 left-8 w-5 h-5 transform rotate-[-30deg]" data-testid="bug-2" />
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-[#0b1120]" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
              What We Do
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-slate-100">
              Our Services<span className="text-red-500">_</span>
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="bento-grid grid-cols-1 lg:grid-cols-12 relative">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className={`service-card bento-item ${service.span} p-6 md:p-8 group relative`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex flex-col h-full">
                  <div className="service-img-container h-48 md:h-56 mb-6 relative">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="service-image w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <service.icon className="w-6 h-6 text-red-500" />
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-200">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-base text-slate-400 leading-relaxed">
                    {service.description}
                  </p>
                </div>
                
                {/* Bug easter egg on one card */}
                {index === 2 && (
                  <Bug className="bug-easter-egg absolute top-4 right-[-6px] w-4 h-4 transform rotate-90" data-testid="bug-3" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding bg-[#0f172a]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto relative">
          <div className="mb-12 md:mb-16 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
              Common Questions
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-slate-100">
              FAQ<span className="text-red-500">_</span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="faq-accordion space-y-2">
            {faqItems.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-slate-800 bg-slate-900/30 px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-slate-200 hover:text-white transition-colors text-left py-5 text-base md:text-lg font-medium">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Hidden Bug #4 - FAQ section */}
          <Bug className="bug-easter-egg absolute bottom-0 right-0 w-4 h-4 transform translate-x-2 translate-y-8" data-testid="bug-4" />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-[#0b1120]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
              Get In Touch
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-slate-100">
              Contact Us<span className="text-red-500">_</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {/* Contact Form */}
            <div className="border border-slate-800 bg-slate-900/30 p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="cybr-input w-full px-4 py-3"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="cybr-input w-full px-4 py-3"
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="cybr-input w-full px-4 py-3"
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                    Service Needed
                  </label>
                  <Select onValueChange={handleServiceChange} value={formData.service}>
                    <SelectTrigger className="cybr-input w-full px-4 py-3" data-testid="contact-service-select">
                      <SelectValue placeholder="Select a service..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {services.map(service => (
                        <SelectItem 
                          key={service.id} 
                          value={service.id}
                          className="text-slate-200 hover:bg-slate-800 focus:bg-slate-800"
                        >
                          {service.title}
                        </SelectItem>
                      ))}
                      <SelectItem 
                        value="other"
                        className="text-slate-200 hover:bg-slate-800 focus:bg-slate-800"
                      >
                        Other / Not Sure
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="cybr-input w-full px-4 py-3 resize-none"
                    placeholder="Describe your device and the issue you're experiencing..."
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-widest px-8 py-6 rounded-none w-full"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col justify-center">
              <div className="contact-terminal space-y-8">
                <div className="border-l-2 border-red-500 pl-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Email
                  </p>
                  <a 
                    href="mailto:cybrdynesystems@gmail.com" 
                    className="text-lg md:text-xl text-slate-200 hover:text-red-500 transition-colors flex items-center gap-3"
                    data-testid="contact-email-link"
                  >
                    <Mail className="w-5 h-5 text-red-500" />
                    cybrdynesystems@gmail.com
                  </a>
                </div>

                <div className="border-l-2 border-red-500 pl-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Phone
                  </p>
                  <a 
                    href="tel:4073351798" 
                    className="text-lg md:text-xl text-slate-200 hover:text-red-500 transition-colors flex items-center gap-3"
                    data-testid="contact-phone-link"
                  >
                    <Phone className="w-5 h-5 text-red-500" />
                    (407) 335-1798
                  </a>
                </div>

                <div className="border-l-2 border-red-500 pl-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Location
                  </p>
                  <p className="text-lg md:text-xl text-slate-200 flex items-center gap-3" data-testid="contact-location">
                    <MapPin className="w-5 h-5 text-red-500" />
                    Serving Central Florida
                  </p>
                </div>

                <div className="mt-12 p-6 border border-slate-800 bg-slate-900/30">
                  <p className="text-slate-400 text-sm leading-relaxed">
                    <span className="text-red-500 font-mono">{'>'}</span> Submit your info and we'll get back to you within 24 hours with a quote or to schedule your repair.
                  </p>
                </div>
              </div>

              {/* Hidden Bug #5 - Contact section */}
              <Bug className="bug-easter-egg mt-12 ml-auto w-4 h-4" data-testid="bug-5" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 bg-[#0b1120] relative" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="logo-placeholder text-lg">
              <span className="text-slate-400">CYBRDYNE</span>
              <span className="text-red-500">_</span>
              <span className="text-slate-600">SYSTEMS</span>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} CybrDyne Systems. All rights reserved.
            </p>
          </div>
        </div>

        {/* Hidden Bug #6 - Footer */}
        <Bug className="bug-easter-egg absolute bottom-4 left-4 w-3 h-3 transform rotate-45" data-testid="bug-6" />
      </footer>
    </div>
  );
};

export default LandingPage;
